import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { PageLayout } from "../../layouts/PageLayout";
import { CardLayout, Invoice, PageHeader } from "../../components/v3";
import { DownloadAndPrint } from "../../components/DownloadAndPrint";
import { get_date } from "../../components/generalComponets";
import { AccountService, FinanceService } from "../../services";
import { getAccountName, getDateAndTime } from "../../utils/formatters";

interface UserInfo {
  firstName: string;
  lastName: string;
}

interface SingleExpenseData {
  id?: string;
  date?: string;
  description?: string;
  amount?: string | number;
  createdBy?: UserInfo;
  createdAt?: string;
  updatedBy?: UserInfo;
  updatedAt?: string;
  project?: { name: string };
  meta?: { fromAccountId: string };
}

export const SingleExpense = () => {
  const [invoiceData, setInvoiceData] = useState<Partial<SingleExpenseData>>({});
  const [account, setAccount] = useState("");

  const { id } = useParams();
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const fetchData = async (expenseId) => {
    try {
      const { data: expense } = await FinanceService.getExpense(expenseId);
      setInvoiceData(expense);

      if (expense?.meta?.fromAccountId) {
        const { data: accountResponse } = await AccountService.getAccountById(expense.meta.fromAccountId);
        setAccount(getAccountName(accountResponse?.type, accountResponse));
      }
    } catch (error) {
      toast.error("Failed to fetch expense details");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  const rows = [
    {
      id: invoiceData?.id,
      date: get_date(invoiceData?.date),
      description: invoiceData?.description,
      amount: invoiceData?.amount,
    },
  ];

  return (
    <PageLayout>
      {/* header section */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <PageHeader title="Expense Details" />
      </Box>

      {/* table section */}
      <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
        <CardLayout>
          <div ref={printRef}>
            <Invoice
              title="Expense"
              sectionB={[
                {
                  label: "Created By",
                  value: invoiceData?.createdBy
                    ? `${invoiceData?.createdBy?.firstName} ${invoiceData?.createdBy?.lastName} (${getDateAndTime(
                        invoiceData?.createdAt,
                      )})`
                    : "N/A",
                },
                invoiceData?.updatedBy && {
                  label: "Last Updated By",
                  value: `${invoiceData?.updatedBy?.firstName ?? ""} ${invoiceData?.updatedBy?.lastName ?? ""} (${getDateAndTime(
                    invoiceData?.updatedAt,
                  )})`,
                },
              ].filter(Boolean)}
              sectionC={[{ label: "Project", value: invoiceData?.project?.name || "N/A" }]}
              sectionD={[{ label: "Payment From", value: account }]}
              columns={[
                { id: "id", label: "Invoice No" },
                { id: "date", label: "Date" },
                { id: "description", label: "Description" },
                { id: "amount", label: "Amount" },
              ]}
              rows={rows}
              subtotal={invoiceData?.amount}
            />
          </div>
        </CardLayout>
      </Box>

      <DownloadAndPrint handlePrint={handlePrint} printRef={printRef} fileName={"SingleExpense.pdf"} />
    </PageLayout>
  );
};
